import { useCallback, useMemo } from 'react';

import { link, routes } from '@aline/routes';
import { matchPath, useNavigate } from 'react-router-dom';
import { DECLARATIVE_MENU_SEPARATOR } from 'src/components/declarative-menu';
import type { DeclarativeMenuConfig } from 'src/components/declarative-menu';
import {
  setEditorFullScreen,
  setShowNonPrintingCharacters,
  setShowPageBreaks,
  setShowRulers,
  useEditorViewPreferences,
} from 'src/editor/vars';
import { useDocumentParams } from 'src/helpers';
import { useNotificationDispatch } from 'src/hooks';
import { useGlobalDrawer } from 'src/layouts';
import type { PushNotification } from 'src/notifications';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

export const fullScreenNotificationPayload: PushNotification = {
  payload: {
    autoHideDuration: 10000,
    key: uuid4(),
    message: 'Press escape to exit full screen.',
    variant: 'info',
  },
  type: NotificationsActionType.PUSH_NOTIFICATION,
};

export function useViewMenu() {
  const dispatch = useNotificationDispatch();
  const { documentId } = useDocumentParams();
  const { fullScreen, showNonPrintingCharacters, showPageBreaks, showRulers } =
    useEditorViewPreferences();
  const { isOpen: isDrawerOpen, toggleDrawer } = useGlobalDrawer();
  const isOutlineActive =
    isDrawerOpen && !!matchPath(routes.DocumentDetailFields, location.pathname);
  const navigate = useNavigate();
  const toggleFullscreen = useCallback(() => {
    setEditorFullScreen(!fullScreen);

    setTimeout((): void => {
      dispatch(fullScreenNotificationPayload);
    }, 500);
  }, [dispatch, fullScreen]);

  const toggleInputOutline = useCallback(() => {
    if (isOutlineActive) {
      toggleDrawer();
    } else {
      navigate(link.DocumentDetailFields({ documentId }), { replace: true });
      if (!isDrawerOpen) toggleDrawer();
    }
  }, [documentId, isDrawerOpen, isOutlineActive, navigate, toggleDrawer]);

  const togglePageBreaks = useCallback(() => setShowPageBreaks(!showPageBreaks), [showPageBreaks]);

  const toggleRuler = useCallback(() => setShowRulers(!showRulers), [showRulers]);

  const toggleShowNonPrintingCharacters = useCallback(
    () => setShowNonPrintingCharacters(!showNonPrintingCharacters),
    [showNonPrintingCharacters]
  );

  return useMemo(
    (): DeclarativeMenuConfig => [
      { checked: showRulers, label: 'Show ruler', onClick: toggleRuler },
      { checked: isOutlineActive, label: 'Show input outline', onClick: toggleInputOutline },
      { checked: showPageBreaks, label: 'Show page breaks', onClick: togglePageBreaks },
      {
        checked: showNonPrintingCharacters,
        label: 'Show non-printing characters',
        onClick: toggleShowNonPrintingCharacters,
      },
      DECLARATIVE_MENU_SEPARATOR,
      { checked: fullScreen, label: 'Fullscreen', onClick: toggleFullscreen },
    ],
    [
      showRulers,
      toggleRuler,
      isOutlineActive,
      toggleInputOutline,
      showPageBreaks,
      togglePageBreaks,
      showNonPrintingCharacters,
      toggleShowNonPrintingCharacters,
      fullScreen,
      toggleFullscreen,
    ]
  );
}
